/**
 * Animated switch-list preview engine (Plan 26 Phase 1d, scope A2).
 *
 * Pure simulation — no React, no Konva. Builds a virtual timeline from a
 * hydrated `SwitchList` (items → micro-steps) plus yard geometry, then
 * answers `snapshotAt(feet)` with loco + car positions in lon/lat for the
 * canvas layer to project.
 *
 * Model:
 *  - Entity state is `carId/'loco' → {trackId, offsetFeet}` in A-end→B-end
 *    space (`linearOffsetFeet` convention).
 *  - A TRAVEL micro-step crosses one junction: tail of fromTrack (trimmed
 *    so the whole consist fits) + toTrack (possibly truncated for spot /
 *    entry-end-pack stops). Movers translate rigidly along the leg — cars
 *    parked ahead of the loco lead on shoves without special-casing.
 *  - COUPLE adds `item.carIds` to the consist at their parked positions;
 *    UNCOUPLE parks them where the leg left them.
 *  - THROW_SWITCH / REVERSE are marker dwells (no motion); REVERSE is
 *    geometrically implicit — leg orientation handles direction changes.
 */

import type { MicroStep, SwitchList, SwitchListItem } from '../types/interchange'
import {
  buildTravelLegPath,
  junctionEnd,
  junctionPointOf,
  pointOnTrackAB,
  positionAtFeet,
  trimOriented,
  type LegPath,
  type LegPosition,
  type SwitchFeatureLike,
  type TrackFeatureLike,
} from './trackPath'
import {
  CLEARANCE_MARGIN_FT,
  COUPLER_GAP_FT,
  COUPLING_REACH_FT,
  DEFAULT_CAR_LENGTH_FT,
  DEFAULT_LOCO_OFFSET_FT,
  LOCO_LENGTH_FT,
  PACK_GAP_FT,
} from './constants'

const LOCO = 'loco'
/** Marker steps consume timeline at a fixed virtual rate (scrub pacing). */
const DWELL_FEET = { THROW_SWITCH: 60, REVERSE: 40, COUPLE: 40, UNCOUPLE: 40, OTHER: 30 } as const

export interface CarStart {
  trackId: string
  offsetFeet: number
  /** Backend AssetType ('LOCOMOTIVE' | car types); absent rows are cars.
   *  Feeds the inventory normalization seam — a locomotive row must never
   *  become a ghost parked body blocking its own first leg. */
  assetType?: string
}

export interface SpotAnchor {
  trackId: string
  startOffsetFeet: number
  endOffsetFeet: number
}

/** Geometry + optional placement data the player needs. */
export interface PreviewGeo {
  tracks: Map<string, TrackFeatureLike>
  switches: Map<string, SwitchFeatureLike>
  /** carId → live A→B offset (from /yards/{f}/tracks/{t}/cars). Cars absent
   *  here are approximated at the COUPLE junction and flagged. */
  carStarts?: Map<string, CarStart>
  /** spotId → offsets (from the spots API), for spot-targeted stops. */
  spots?: Map<string, SpotAnchor>
  /** Rail-car id of the locomotive powering this list. Its inventory row
   *  seeds the 'loco' entity at its real position instead of a ghost body
   *  that would block the loco's own first leg. When absent, a sole
   *  LOCOMOTIVE row is adopted; zero/ambiguous rows leave locoPos null
   *  (the legacy first-leg seating path). */
  locomotiveId?: string
}

interface EntityPos {
  trackId: string
  /** A→B offset — head position for movers/drops, A-end edge for
   *  inventory (`rail_cars.linear_offset` convention). */
  offsetFeet: number
  /** Body extent in A→B feet — authoritative for occupancy/overlap math,
   *  regardless of which convention `offsetFeet` follows. */
  startFeet: number
  endFeet: number
  /** Renderer anchoring: 'forward' = body extends toward +A→B
   *  (inventory edge convention); 'trail' = body trails the head
   *  (movers / dropped cars). */
  bodyDir: 'forward' | 'trail'
  /** Heading the entity was facing when parked — dropped cars keep their
   *  travel direction so the renderer anchors the body behind the head. */
  headAngleDeg?: number
}

/** One timeline entry — a TRAVEL leg or a marker dwell. */
export interface TimelineStep {
  itemIndex: number
  stepIndex: number
  step: MicroStep
  startFeet: number
  spanFeet: number
  leg?: LegPath
  /** arc position of each mover at leg start (loco + consist members). */
  moverArcs?: Map<string, number>
  movers?: Set<string>
  /** World state to render during this step (post-action for markers). */
  parkedAtStart: Map<string, EntityPos>
  consistAtStart: Set<string>
  /** switchId → last thrown state at step start (persists across passes). */
  switchStatesAtStart: Record<string, string>
  /** TEMP-UC1-APPROACH — display-only same-track approach drawn inside a
   *  COUPLE marker's span for legacy wires (loco already on the couple
   *  track → expander emits no reposition leg). NOT a switch-list step;
   *  the UI labels it illustrative and it never enters list totals.
   *  Remove when the backend emits an explicit approach TRAVEL — backlog:
   *  "Explicit approach leg in the plan: backend loco offset + approach
   *  TRAVEL + virtualCutAnchor" (Plan 27 Stage 2 / actualPathFeet /
   *  three-models parity). */
  syntheticApproachFeet?: number
}

export interface PreviewTimeline {
  steps: TimelineStep[]
  totalFeet: number
  /** Missing tracks/switches/car geometry → the UI shows a notice and may
   *  still play when `fatal` is false. */
  staleReasons: string[]
  fatal: boolean
  /** Entities ever in the consist — the render "cast". */
  castCarIds: Set<string>
  /** Cars approximated at couple time (no inventory position). */
  approxCarIds: Set<string>
  /** Ordered per-task carIds for consist ordering. */
  itemCarIds: Map<number, string[]>
  /** Passes skipped because the backend marked them BLOCKED — their cars
   *  stay parked and the skip is surfaced, never rendered as motion. Also
   *  carries passes not shown because they depend on a pass that could
   *  not be animated (blocked or inconsistent). */
  blockedPasses: { itemIndex: number; label: string; reason: string }[]
  /** Set when an enforced invariant stopped the build — the timeline ends
   *  where the world stopped being trustworthy. The pass that broke is
   *  NOT in blockedPasses (it may have partially animated). */
  inconsistency?: { itemIndex: number; label: string; reason: string }
}

export interface PreviewCarSnapshot {
  carId: string
  pos: LegPosition
  inConsist: boolean
  approx: boolean
  /** 'trail' = body trails the head point; 'forward' = body extends
   *  toward +A→B (inventory edge offsets — no recorded heading). */
  bodyDir: 'trail' | 'forward'
  /** Ambient body — parked inventory that never joins the consist
   *  (F-UI-1). Rendered dimmed so blocking cars are visible instead of
   *  the yard looking empty where the leg physically stalls. */
  ambient?: boolean
}

export interface PreviewSnapshot {
  loco: LegPosition | null
  cars: PreviewCarSnapshot[]
  /** Parked non-cast bodies at this moment (F-UI-1) — cars that occupy
   *  track space and may be why a pass blocked, but are never animated. */
  ambientCars: PreviewCarSnapshot[]
  activeStepIndex: number // index into timeline.steps
  /** (itemIndex, stepIndex) for micro-step list sync. */
  activeItemIndex: number
  activeMicroStepIndex: number
  activeStep: MicroStep | null
  /** TEMP-UC1-APPROACH — feet of the display-only same-track approach
   *  drawn inside the active COUPLE marker's span (undefined when the
   *  active step has none). The transport caption labels it
   *  'Approach cut - illustrative, not a switch-list step'. */
  activeSyntheticApproachFeet?: number
  shoving: boolean
  /** switchId → state overrides at this moment (for glyph coloring). */
  switchStates: Record<string, string>
}

/** Per-car metadata: `lengthFeet` feeds the sim; the rest feeds renderer
 *  labels/badges and is ignored by the physics. */
export interface CarMeta {
  lengthFeet: number
  reportingMark?: string
  hazmatClass?: string
  hazardous?: boolean
}

export interface BuildTimelineResult {
  ok: boolean
  timeline?: PreviewTimeline
  staleReasons: string[]
  fatal: boolean
  /** Passes skipped as BLOCKED or not-shown dependents — also present on
   *  the timeline when built. */
  blockedPasses: { itemIndex: number; label: string; reason: string }[]
  /** Invariant halt, if any — "preview inconsistent: <reason>". */
  inconsistency?: { itemIndex: number; label: string; reason: string }
}

const markerDwell = (type?: string): number => {
  switch (type) {
    case 'THROW_SWITCH':
      return DWELL_FEET.THROW_SWITCH
    case 'REVERSE':
      return DWELL_FEET.REVERSE
    case 'COUPLE':
    case 'UNCOUPLE':
      return DWELL_FEET.COUPLE
    default:
      return DWELL_FEET.OTHER
  }
}

/**
 * Build the preview timeline. Fails closed (`ok: false`) when a TRAVEL leg
 * references a track not present in `geo.tracks` — per the plan's stale-map
 * guard, missing geometry disables playback rather than guessing.
 */
export const buildPreviewTimeline = (
  list: SwitchList,
  geo: PreviewGeo,
  carMeta: Map<string, CarMeta> = new Map()
): BuildTimelineResult => {
  const stale: string[] = []
  let fatal = false
  const steps: TimelineStep[] = []
  const castCarIds = new Set<string>()
  const approxCarIds = new Set<string>()
  const itemCarIds = new Map<number, string[]>()
  const blockedPasses: { itemIndex: number; label: string; reason: string }[] = []

  // World state mutated as the timeline is built.
  const parked = new Map<string, EntityPos>()
  const consist = new Set<string>()
  // Approximated-car placement frontier: `${trackId}:${junctionEnd}` →
  // next free depth in feet from that end. Persists across steps/items so
  // later COUPLEs never re-cover earlier approximated cars.
  const approxFrontier = new Map<string, number>()
  const lenOf = (id: string) => carMeta.get(id)?.lengthFeet ?? DEFAULT_CAR_LENGTH_FT
  /** Body extent of a mover at `pos`: a `flipped` segment is traversed
   *  B→A so the head is the AB-low end (body toward +A→B); otherwise the
   *  head is AB-high (body toward −A→B). */
  const bodyInterval = (pos: LegPosition, lenFeet: number) =>
    pos.flipped
      ? { startFeet: pos.offsetFeet, endFeet: pos.offsetFeet + lenFeet }
      : { startFeet: pos.offsetFeet - lenFeet, endFeet: pos.offsetFeet }
  /** True when a body placed at `pos` would overhang either end of the
   *  track it is recorded on — i.e. its interval falls outside
   *  [0, trackLength]. At a leg END that means resting across a track
   *  boundary (the switch): physically a foul, visually the diagonal-car
   *  defect. Mid-leg a consist legitimately spans the junction while
   *  moving, so this is checked only on committed end positions. */
  const straddlesTrackEnd = (pos: LegPosition, lenFeet: number): boolean => {
    const segLen = geo.tracks.get(pos.trackId)?.properties?.lengthFeet ?? 2000
    const iv = bodyInterval(pos, lenFeet)
    return iv.startFeet < -0.5 || iv.endFeet > segLen + 0.5
  }
  /** Operator-facing names for ids embedded in reason strings — raw
   *  entity ids never leave this function in user-facing text. Track
   *  and switch names come off the GeoJSON properties; car names off
   *  carMeta (reporting mark). Unknown ids fall through verbatim so the
   *  modal's uuid→mark rewrite still gets a chance. */
  const trackName = (id: string | undefined | null): string =>
    (id != null ? geo.tracks.get(id)?.properties?.name : undefined) ?? (id ?? '?')
  const switchName = (id: string | undefined | null): string =>
    (id != null ? geo.switches.get(id)?.properties?.name : undefined) ?? (id ?? '?')
  const carMark = (id: string | undefined | null): string => {
    if (id == null) return '?'
    if (id === LOCO) return 'the locomotive'
    return carMeta.get(id)?.reportingMark
      ?? carMeta.get(id.toLowerCase())?.reportingMark
      ?? id
  }
  /** switchId → last thrown state (overrides only; seeded states live on
   *  the geometry itself and apply when there is no override). */
  const switchStates: Record<string, string> = {}
  let locoPos: EntityPos | null = null
  let cursor = 0

  // Inventory normalization seam — the ONE place raw /track-cars rows
  // become world entities. Classification:
  //   geo.locomotiveId row (or the sole LOCOMOTIVE row when unspecified)
  //     → the sim 'loco' entity seeded at its real offset — never a
  //       parked ghost that would hard-stop the loco's own first leg.
  //   every other row (all car types AND non-selected locomotives)
  //     → a parked body. It occupies track space (delivery packing,
  //       occupancy cap, hard stop) but never moves and never couples —
  //       only cast carIds can join the consist, and a railcar id never
  //       aliases the 'loco' key.
  // Backend AssetType has exactly one non-car value (LOCOMOTIVE); unknown
  // types are treated as cars — a body that occupies space is safer than
  // an invisible hole.
  {
    const locoRows = [...(geo.carStarts ?? new Map<string, CarStart>())]
      .filter(([, s]) => s.assetType === 'LOCOMOTIVE')
    let seedId: string | undefined
    if (geo.locomotiveId) {
      const s = geo.carStarts?.get(geo.locomotiveId)
      if (!s) {
        stale.push(
          `locomotive '${carMark(geo.locomotiveId)}' not found in track inventory — starts unseated at first leg`
        )
      } else {
        seedId = geo.locomotiveId
      }
    } else if (locoRows.length === 1) {
      seedId = locoRows[0][0]
    } else if (locoRows.length > 1) {
      stale.push(
        `${locoRows.length} locomotives in track inventory and none identified — all treated as parked obstacles`
      )
    } else {
      // No id was given AND no loco row sits on a fetched track — the sim
      // will seat an unseated loco at the first leg. That must not be
      // silent: the operator sees a loco that wasn't actually there.
      stale.push(
        `no locomotive identified for this list — drawn as unseated at the first leg`
      )
    }
    geo.carStarts?.forEach((start, id) => {
      if (id === seedId) {
        const locoLen = carMeta.get(id)?.lengthFeet ?? LOCO_LENGTH_FT
        locoPos = {
          trackId: start.trackId,
          offsetFeet: start.offsetFeet,
          startFeet: start.offsetFeet,
          endFeet: start.offsetFeet + locoLen,
          bodyDir: 'forward',
        }
        // Mirror into parked so the seeded loco draws from frame 0
        // (previously it appeared only once a leg seated it).
        parked.set(LOCO, locoPos)
        return
      }
      parked.set(id, {
        trackId: start.trackId,
        offsetFeet: start.offsetFeet,
        startFeet: start.offsetFeet,
        endFeet: start.offsetFeet + lenOf(id),
        bodyDir: 'forward',
      })
    })
  }

  // Data-quality gate: overlapping inventory bodies are reported via
  // staleReasons, never silently corrected in the display.
  {
    const byTrack = new Map<string, { id: string; start: number; end: number }[]>()
    geo.carStarts?.forEach((s, id) => {
      const c = { id, start: s.offsetFeet, end: s.offsetFeet + lenOf(id) }
      byTrack.set(s.trackId, [...(byTrack.get(s.trackId) ?? []), c])
    })
    for (const [trackId, cars] of byTrack) {
      cars.sort((a, b) => a.start - b.start)
      for (let i = 1; i < cars.length; i++) {
        if (cars[i].start < cars[i - 1].end - 0.5) {
          stale.push(
            `data-quality: '${carMark(cars[i - 1].id)}' and '${carMark(cars[i].id)}' overlap on '${trackName(trackId)}'`
          )
        }
      }
    }
  }

  const items = list.items ?? []
  items.forEach((item, itemIndex) => {
    itemCarIds.set(itemIndex, item.carIds ?? [])
    for (const id of item.carIds ?? []) castCarIds.add(id)
  })

  const pushMarker = (
    itemIndex: number,
    stepIndex: number,
    step: MicroStep,
    /** TEMP-UC1-APPROACH — same-track approach leg drawn inside the
     *  marker span (display-only; see TimelineStep.syntheticApproachFeet). */
    approach?: { leg: LegPath; feet: number }
  ) => {
    const spanFeet = markerDwell(step.type) + (approach?.feet ?? 0)
    steps.push({
      itemIndex,
      stepIndex,
      step,
      startFeet: cursor,
      spanFeet,
      leg: approach?.leg,
      moverArcs: approach ? new Map([[LOCO, 0]]) : undefined,
      movers: approach ? new Set([LOCO]) : undefined,
      parkedAtStart: new Map(parked),
      consistAtStart: new Set(consist),
      switchStatesAtStart: { ...switchStates },
      syntheticApproachFeet: approach?.feet,
    })
    cursor += spanFeet
  }

  const labelOf = (i: number, it?: SwitchListItem) =>
    it?.passSummary ?? it?.summary ?? `Pass ${i + 1}`

  /** Invariant halt — "preview inconsistent: <reason>". The timeline ends
   *  where the world stopped being trustworthy; every pass after an
   *  un-animatable one was planned assuming it ran, so they are surfaced
   *  as not-shown rather than drawn against a diverged world. */
  let inconsistency:
    | { itemIndex: number; label: string; reason: string }
    | undefined
  const halt = (itemIndex: number, item: SwitchListItem | undefined, reason: string) => {
    inconsistency = { itemIndex, label: labelOf(itemIndex, item), reason }
    stale.push(`preview inconsistent: ${reason}`)
  }
  const cascadeNotShown = (fromItemIndex: number, reason: string) => {
    for (let k = fromItemIndex + 1; k < items.length; k++) {
      const it = items[k]
      // A dependent that is itself BLOCKED keeps its backend reason — it
      // was never going to animate regardless of the earlier stop.
      const r = it.status === 'BLOCKED'
        ? (it.statusReason ?? 'blocked by backend validation')
        : reason
      blockedPasses.push({ itemIndex: k, label: labelOf(k, it), reason: r })
    }
  }
  const endPass = (itemIndex: number) => {
    if (inconsistency) {
      cascadeNotShown(itemIndex, `not shown — playback stopped: ${inconsistency.reason}`)
      return true
    }
    return false
  }

  for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
    const item = items[itemIndex]
    const itemLabel = labelOf(itemIndex, item)
    // Junction anchor of the most recent TRAVEL in this item — the
    // fallback a legacy COUPLE (no switchId on the wire) uses to find the
    // pull-side end of its track, mirroring PlanValidator.pullEnd.
    let lastEntry: {
      toTrackId?: string
      junction?: ReturnType<typeof junctionPointOf> | null
    } | undefined

    // The plan verdict is authoritative: a pass the backend marked BLOCKED
    // is never animated — its cars stay parked and the skip is surfaced
    // instead of drawing the crew a move they were told not to make. And
    // playback STOPS here: later passes were planned assuming this one
    // executed, so animating them would fabricate a world that cannot
    // exist.
    if (item.status === 'BLOCKED') {
      const reason = item.statusReason ?? 'blocked by backend validation'
      blockedPasses.push({ itemIndex, label: itemLabel, reason })
      stale.push(`blocked-pass: '${itemLabel}' skipped — ${reason}`)
      cascadeNotShown(itemIndex, `not shown — depends on blocked pass '${itemLabel}'`)
      break
    }

    // Invariant (e): the pass's required start state must match the sim
    // world — its named cars stand where the plan expects them. A car on
    // another track means the world diverged from the plan (skipped or
    // incomplete earlier pass); animate nothing further.
    if (item.sourceTrackId) {
      const stray = (item.carIds ?? []).find(id => {
        const p = parked.get(id)
        return p != null && p.trackId !== item.sourceTrackId
      })
      if (stray) {
        halt(
          itemIndex,
          item,
          `pass start state mismatch — '${carMark(stray)}' is on '${trackName(parked.get(stray)!.trackId)}', the plan expects it on '${trackName(item.sourceTrackId)}'`
        )
      }
    }
    if (endPass(itemIndex)) break

    const micro = item.microSteps ?? []
    for (let stepIndex = 0; stepIndex < micro.length; stepIndex++) {
      const step = micro[stepIndex]
      if (step.type !== 'TRAVEL') {
        // Apply semantic effect first so parkedAtStart renders post-action.
        let coupleApproach: { leg: LegPath; feet: number } | undefined
        let coupleHalt: string | undefined
        if (step.type === 'COUPLE') {
          // APPROXIMATE POSITIONS: real inventory offsets come from
          // geo.carStarts; anything parked by this branch is a guess and
          // is flagged via approxCarIds (dashed marker in the renderer).
          // The stagger frontier persists per track+junction-end across
          // steps AND items — a per-step depth would re-cover cars a
          // previous COUPLE already parked at the junction.
          // One plain-language notice per COUPLE step when the junction is
          // unresolvable — never a silent guess, never one line per car.
          // Materialize BEFORE the loco/coupling check so the cut is real:
          // the reach check and the approach-leg anchor both need it.
          const approxPlaced: string[] = []
          let approxEndIsFirst = true
          let approxTrackLabel = ''
          // A switchless COUPLE can sit at step 0 on the real wire — the
          // expander emits no reposition leg when the loco already stands
          // on the couple track, so there is no entry TRAVEL to anchor on.
          // The exit leg (next TRAVEL leaving this track) is the pull
          // junction — the end the cut gets dragged out through.
          const exitJunction = (() => {
            const exitLeg = micro
              .slice(stepIndex + 1)
              .find(
                s =>
                  s.type === 'TRAVEL' &&
                  s.fromTrackId === step.toTrackId &&
                  s.switchId != null
              )
            return exitLeg?.switchId != null
              ? junctionPointOf(geo.switches.get(exitLeg.switchId))
              : null
          })()
          // Junction anchor for this couple — same precedence the cars
          // materialize against (switchId → entry TRAVEL → exit TRAVEL).
          const coupleJunction = step.switchId
            ? junctionPointOf(geo.switches.get(step.switchId))
            : lastEntry && step.toTrackId != null && lastEntry.toTrackId === step.toTrackId
              ? (lastEntry.junction ?? null)
              : exitJunction
          const hasSeatedCutCar = (item.carIds ?? []).some(id => parked.has(id))
          for (const id of item.carIds ?? []) {
            consist.add(id)
            if (!parked.has(id)) {
              approxCarIds.add(id)
              const track = step.toTrackId ? geo.tracks.get(step.toTrackId) : undefined
              if (track) {
                const len = track.properties?.lengthFeet ?? 2000
                const carLen = lenOf(id)
                const j = coupleJunction
                const fromFirst = j ? junctionEnd(track, j) === 'first' : true
                if (!j) {
                  approxPlaced.push(carMark(id))
                  approxEndIsFirst = fromFirst
                  approxTrackLabel = trackName(track.id)
                }
                const fKey = `${track.id}:${fromFirst ? 'first' : 'last'}`
                // Depth space: feet from the junction end inward. Advance
                // past any parked body overlapping the new car's slot.
                let depth = Math.max(approxFrontier.get(fKey) ?? COUPLER_GAP_FT, COUPLER_GAP_FT)
                let placing = true
                while (placing) {
                  placing = false
                  for (const [pid, p] of parked) {
                    if (pid === LOCO || p.trackId !== track.id) continue
                    const bs = fromFirst ? p.startFeet : len - p.endFeet
                    const be = fromFirst ? p.endFeet : len - p.startFeet
                    if (be > depth && bs < depth + carLen) {
                      depth = be + COUPLER_GAP_FT
                      placing = true
                    }
                  }
                }
                const offset = fromFirst ? depth : len - depth
                parked.set(id, {
                  trackId: track.id,
                  offsetFeet: offset,
                  ...(fromFirst
                    ? { startFeet: offset, endFeet: offset + carLen, bodyDir: 'forward' as const }
                    : { startFeet: offset - carLen, endFeet: offset, bodyDir: 'trail' as const }),
                })
                approxFrontier.set(fKey, depth + carLen + COUPLER_GAP_FT)
              }
            }
          }
          if (approxPlaced.length > 0) {
            stale.push(
              `step ${stepIndex}: ${approxPlaced.join(', ')} shown ` +
              `approximately at the ${approxEndIsFirst ? 'first' : 'last'} end ` +
              `of '${approxTrackLabel}' — COUPLE carries no switch id`
            )
          }

          // Invariant (b): coupling needs the loco on the cut's track
          // within coupling reach of its nearest car. A loco KNOWN to be
          // elsewhere makes the couple impossible — halt rather than
          // animate cars alone.
          const cut = (item.carIds ?? [])
            .map(id => parked.get(id))
            .filter((p): p is EntityPos => p != null)
          const cutTrack = step.toTrackId ?? cut[0]?.trackId

          // Invariant (b): a cross-track halt needs a PRE-SEATED cut car
          // to prove the cut's track — a just-materialized body carries
          // the wire's own claim and can't prove the loco is elsewhere
          // (the leg-level invariant (a) owns that case instead).
          if (locoPos && cut.length > 0 && cutTrack != null) {
            if (locoPos.trackId !== cutTrack && hasSeatedCutCar) {
              halt(
                itemIndex,
                item,
                `COUPLE: loco is on '${trackName(locoPos.trackId)}' but the cut stands on '${trackName(cutTrack)}'`
              )
              break
            }
            const gapOf = (p: EntityPos) =>
              Math.max(locoPos!.startFeet - p.endFeet, p.startFeet - locoPos!.endFeet)
            const sameTrack: EntityPos[] = locoPos.trackId === cutTrack
              ? cut.filter(p => p.trackId === cutTrack)
              : []
            if (sameTrack.length > 0) {
              const nearest: EntityPos = sameTrack.reduce((a, b) => (gapOf(b) < gapOf(a) ? b : a))
              const locoLen: number = locoPos.endFeet - locoPos.startFeet
              const gap: number = gapOf(nearest)
              if (gap > COUPLING_REACH_FT) {
                // TEMP-UC1-APPROACH (a): the crew reads "couple at X" as
                // "drive to the cut first" — the wire has no leg for it
                // (expander emits none for same-track), so the marker
                // span draws the approach: loco head travels to a coupler
                // gap off the nearest cut edge, capped a coupler gap
                // short of any parked body in the path. When a blocker
                // leaves the cut out of reach the honest answer is still
                // a halt — drawing the approach through it would be
                // worse than the old silent standoff.
                const below: boolean = locoPos.endFeet <= nearest.startFeet
                const trackFeat = geo.tracks.get(cutTrack)
                const trackLen: number = trackFeat?.properties?.lengthFeet ?? 2000
                const standoff: number = below
                  ? nearest.startFeet - COUPLER_GAP_FT
                  : nearest.endFeet + COUPLER_GAP_FT
                let target: number = standoff
                let blockerId: string | undefined
                for (const [pid, p] of parked) {
                  if (pid === LOCO || p.trackId !== cutTrack || (item.carIds ?? []).includes(pid)) continue
                  if (below && p.endFeet > locoPos.endFeet && p.startFeet < target) {
                    target = Math.max(p.startFeet - COUPLER_GAP_FT, locoPos.endFeet)
                    blockerId = pid
                  } else if (!below && p.startFeet < locoPos.startFeet && p.endFeet > target) {
                    target = Math.min(p.endFeet + COUPLER_GAP_FT, locoPos.startFeet)
                    blockerId = pid
                  }
                }
                const landsGap = below ? nearest.startFeet - target : target - nearest.endFeet
                if (landsGap > COUPLING_REACH_FT) {
                  // Stop-short: the marker still draws the approach up to
                  // the cap, then the build halts with the classic
                  // beyond-reach reason — a blocker between loco and cut
                  // is a genuine inconsistency, never teleported through.
                  if (blockerId) {
                    stale.push(
                      `step ${stepIndex}: approach on '${trackName(cutTrack)}' stops a coupler gap short of parked body '${carMark(blockerId)}' — the cut stays out of reach`
                    )
                  }
                  coupleHalt = `COUPLE: loco is ${Math.round(landsGap)} ft from the nearest car on '${trackName(cutTrack)}' — beyond coupling reach`
                }
                const feet = below ? target - locoPos.endFeet : locoPos.startFeet - target
                if (feet > 1 && trackFeat) {
                  const base = trackFeat.geometry.coordinates as [number, number][]
                  const oriented = below ? base : [...base].reverse()
                  const orientedStart = below ? locoPos.endFeet : trackLen - locoPos.startFeet
                  coupleApproach = {
                    feet,
                    leg: {
                      segments: [{
                        trackId: cutTrack,
                        coords: trimOriented(oriented, trackLen, orientedStart, orientedStart + feet),
                        spanFeet: feet,
                        trackLengthFeet: trackLen,
                        startFeet: 0,
                        orientedStartFeet: orientedStart,
                        flipped: !below,
                      }],
                      totalFeet: feet,
                      junctionGapDeg: 0,
                      junctionFromSwitchPoint: true,
                      junctionGapExceeded: false,
                    },
                  }
                  stale.push(
                    `step ${stepIndex}: approach cut on '${trackName(cutTrack)}' — illustrative, not a switch-list step`
                  )
                }
                const lp: EntityPos = locoPos
                locoPos = below
                  ? {
                      ...lp,
                      startFeet: target - locoLen,
                      endFeet: target,
                      offsetFeet: lp.bodyDir === 'forward' ? target - locoLen : target,
                    }
                  : {
                      ...lp,
                      startFeet: target,
                      endFeet: target + locoLen,
                      offsetFeet: target,
                    }
                parked.set(LOCO, locoPos)
              } else {
                // Coupling closes the approach: the leg before this marker
                // seats the loco a loco-length short of the cut so the pick-up
                // is readable; a real crew then eases in and makes the joint.
                // Mirror that here — park the loco a coupler gap off the
                // nearest cut car on the side it stands, else the standoff
                // persists as a permanent loco↔cut gap through every leg.
                // Movers translate rigidly, so this is the last honest place
                // to close it. Overlap (degenerate data) is left untouched.
                let snapped: EntityPos = locoPos
                if (locoPos.endFeet <= nearest.startFeet) {
                  const end = nearest.startFeet - COUPLER_GAP_FT
                  snapped = {
                    ...locoPos,
                    startFeet: end - locoLen,
                    endFeet: end,
                    // 'forward' anchors at the A-edge; 'trail' at the head,
                    // which now faces the car (AB-high edge).
                    offsetFeet: locoPos.bodyDir === 'forward' ? end - locoLen : end,
                  }
                } else if (locoPos.startFeet >= nearest.endFeet) {
                  const start = nearest.endFeet + COUPLER_GAP_FT
                  snapped = {
                    ...locoPos,
                    startFeet: start,
                    endFeet: start + locoLen,
                    offsetFeet: start,
                  }
                }
                locoPos = snapped
                // parked['loco'] mirrors locoPos post-leg (occupancy + the
                // place() render path for markers) — keep that invariant.
                parked.set(LOCO, snapped)
              }
            }
          }
        } else if (step.type === 'UNCOUPLE') {
          for (const id of item.carIds ?? []) consist.delete(id)
        }
        // THROW_SWITCH persists: the glyph keeps its last thrown state
        // across items/passes until a later throw changes it back.
        if (step.type === 'THROW_SWITCH' && step.switchId && step.requiredSwitchState) {
          switchStates[step.switchId] = step.requiredSwitchState
        }
        pushMarker(itemIndex, stepIndex, step, coupleApproach)
        if (coupleHalt != null) {
          halt(itemIndex, item, coupleHalt)
          break
        }
        continue
      }

      // TRAVEL leg — resolve geometry.
      const fromTrack = step.fromTrackId ? geo.tracks.get(step.fromTrackId) : undefined
      const toTrack = step.toTrackId ? geo.tracks.get(step.toTrackId) : undefined
      if (!toTrack) {
        stale.push(`step ${stepIndex}: missing toTrack '${step.toTrackId ?? '?'}'`)
        fatal = true
        break
      }
      const junction =
        (step.switchId ? junctionPointOf(geo.switches.get(step.switchId)) : null) ??
        undefined
      if (step.switchId && !junction) {
        stale.push(`step ${stepIndex}: missing switch '${switchName(step.switchId)}'`)
      }
      lastEntry = { toTrackId: step.toTrackId ?? undefined, junction }
      if (fromTrack == null && junction == null) {
        stale.push(`step ${stepIndex}: no junction anchor`)
        fatal = true
        break
      }

      // Movers on the from-track (loco + consist members parked there).
      const movers = new Set<string>()
      const trackFeet = toTrack.properties?.lengthFeet ?? 2000
      let flipped = false
      let tailStart = 0
      let enterAB: number | undefined
      /** Head-to-tail span of the movers on the from-track, in feet —
       *  bodies + coupling standoffs, ≥ Σ lengths. Feeds REVERSE
       *  clearance so the tail car clears the foul point too. */
      let moverSpanFeet = 0
      /** Travel cap when a parked body blocks the sweep on the from-track
       *  — a real hard stop, not an advisory: the lead mover halts a
       *  coupler gap short of the blocker. */
      let hardStopFeet = Infinity
      /** Body that set the cap — named in the halt reason. */
      let hardStopperId: string | undefined

      if (fromTrack && junction) {
        flipped = junctionEnd(fromTrack, junction) === 'first'
        const fromLen = fromTrack.properties?.lengthFeet ?? 2000
        if (locoPos?.trackId === fromTrack.id) movers.add(LOCO)
        for (const id of consist) {
          const p = parked.get(id)
          if (p?.trackId === fromTrack.id) movers.add(id)
        }
        tailStart = Infinity
        let headMax = 0
        for (const id of movers) {
          const p = id === LOCO ? locoPos! : parked.get(id)!
          // Edges in oriented space (feet from the entry end): tail is the
          // entry-side edge, head the junction-side edge. For a flipped
          // B→A traversal offsetFeet alone is the A-edge/head and would
          // trim the rear body off the path.
          tailStart = Math.min(tailStart, flipped ? fromLen - p.endFeet : p.startFeet)
          headMax = Math.max(headMax, flipped ? fromLen - p.startFeet : p.endFeet)
        }
        if (tailStart === Infinity) tailStart = 0
        moverSpanFeet = Math.max(headMax - tailStart, 0)
        enterAB = flipped ? fromLen - tailStart : tailStart

        // F-SWL-2: parked bodies are obstacles on the SOURCE track too.
        // Every mover sweeps the oriented span from its tail edge to the
        // junction — a non-mover body in that span means the leg draws the
        // consist through standing cars (e.g. a loco trapped behind the
        // cut it just dropped). Flag, don't render-through silently.
        for (const [pid, p] of parked) {
          if (p.trackId !== fromTrack.id || movers.has(pid)) continue
          const bs = flipped ? fromLen - p.endFeet : p.startFeet
          const be = flipped ? fromLen - p.startFeet : p.endFeet
          if (be > tailStart && bs < fromLen) {
            // Invariant (c): a body overlapping the consist span itself —
            // inside or atop a mover's occupied space — is an impossible
            // state (shared offsets, drifted seed). Halt, don't draw it.
            if (bs < headMax) {
              halt(
                itemIndex,
                item,
                `parked body '${carMark(pid)}' overlaps a mover on '${trackName(fromTrack.id)}' — inventory positions disagree with the plan`
              )
              break
            }
            stale.push(
              `step ${stepIndex}: leg hard-stops short of parked body '${carMark(pid)}' on '${trackName(fromTrack.id)}' — consist path blocked`
            )
            // A body AHEAD of the lead mover's head caps travel — record
            // the one that binds the cap (argmin), not the last iterated.
            const cap = bs - PACK_GAP_FT - headMax
            if (cap < hardStopFeet) {
              hardStopFeet = cap
              hardStopperId = pid
            }
          }
        }
      }
      if (inconsistency) break

      // Invariant (a): a coupled cut never moves without the locomotive.
      // The loco is absent from the mover set only when it is KNOWN to
      // stand on another track (an unseated loco gets seated below). A
      // leg whose movers are consist cars but not the loco is a diverged
      // world — halt rather than draw cars gliding on their own.
      if (locoPos != null && !movers.has(LOCO) && movers.size > 0) {
        halt(
          itemIndex,
          item,
          `cut departs '${trackName(fromTrack!.id)}' without the locomotive — loco stands on '${trackName(locoPos.trackId)}'`
        )
        break
      }

      // Stop offset on toTrack — keyed on the NEXT step, mirroring the
      // real railroading sequence:
      //   COUPLE   → stop just short of the parked cut
      //   REVERSE  → pull consist + margin past the junction (clear foul)
      //   UNCOUPLE → spot anchor when resolvable, else far-end pack
      //   else     → full traversal (reposition legs)
      const consistLenFeet =
        [...consist].reduce((acc, id) => acc + lenOf(id), 0) + LOCO_LENGTH_FT
      /** Clear entry span on the destination when the next step is an
       *  UNCOUPLE — feet from the junction to the nearest standing body.
       *  Filled by the delivery branch; consumed after moverArcs seat
       *  the loco (the required span depends on whether the loco leads
       *  onto the track). */
      let destStopDepthFeet: number | undefined
      let destBlockerId: string | undefined
      /** Clear span from the entry junction to the nearest cut member on
       *  a COUPLE approach — checked once moverArcs seat the loco (the
       *  loco only counts when it leads onto the track). */
      let coupleSpanFeet: number | undefined
      let coupleNearestId: string | undefined
      let exitFeet: number | undefined
      const toLen = trackFeet
      const nextType = micro[stepIndex + 1]?.type
      const spot = item.targetSpotId ? geo.spots?.get(item.targetSpotId) : undefined
      const jEnd = junction ? junctionEnd(toTrack, junction) : undefined

      // Parked bodies on the destination that are NOT in the moving
      // consist — the occupancy the arriving leg must stop short of.
      // Depth is measured from the ENTRY (junction) end; the loco counts
      // when parked on this track (it is never a consist member). On a
      // COUPLE approach the cut members are the destination — the
      // approach leg closes to a coupler gap on them, so they do not
      // cap the leg; every other body still does.
      const coupleTargets = nextType === 'COUPLE'
        ? new Set(item.carIds ?? [])
        : null
      let nearestOccDepth = Infinity
      let nearestOccId: string | undefined
      if (jEnd) {
        for (const [pid, p] of parked) {
          if (p.trackId !== toTrack.id || consist.has(pid)) continue
          if (coupleTargets?.has(pid)) continue
          const depthStart = jEnd === 'first' ? p.startFeet : toLen - p.endFeet
          if (depthStart < nearestOccDepth) {
            nearestOccDepth = depthStart
            nearestOccId = pid
          }
        }
      }

      if (junction && nextType === 'UNCOUPLE' && spot && spot.trackId === toTrack.id) {
        const mid = (spot.startOffsetFeet + spot.endOffsetFeet) / 2
        exitFeet = jEnd === 'first'
          ? Math.min(mid + consistLenFeet / 2, toLen)
          : Math.max(mid - consistLenFeet / 2, 0)
      } else if (junction && nextType === 'UNCOUPLE') {
        // DELIVERY CONVENTION (shove-in-from-entry): the cut continues in
        // its direction of travel until the lead car's head rests against
        // the far end — or a coupler gap short of the NEAREST spotted car.
        // Successive passes therefore pack toward the entry end, and read
        // from the entry (switch) end the order is latest-pass first
        // (…P3, P2, P1). The loco trails the cut, so it never travels
        // through cars already on the track.
        const stopDepth =
          nearestOccDepth === Infinity
            ? toLen
            : Math.max(nearestOccDepth - PACK_GAP_FT, 0)
        // Whether the consist fits this span is decided once the loco
        // is seated in moverArcs — a shove leaves the loco on the source
        // track, a pull-in needs it inside the span.
        destStopDepthFeet = stopDepth
        destBlockerId = nearestOccId
        exitFeet = jEnd === 'first' ? stopDepth : toLen - stopDepth
      } else if (junction && nextType === 'REVERSE') {
        // Clear the foul point with the WHOLE consist past the switch —
        // sized on the mover span (head-to-tail including standoffs), not
        // Σ lengths; a shorter stop strands tail cars on the source.
        // A locoPos==null consist leg seats the loco ahead of the lead
        // car, so its body + coupler gap extend the span too.
        const span =
          Math.max(moverSpanFeet, consistLenFeet) +
          (locoPos == null && moverSpanFeet > 0 ? COUPLER_GAP_FT + LOCO_LENGTH_FT : 0)
        const clearFeet = span + CLEARANCE_MARGIN_FT
        exitFeet = jEnd === 'first'
          ? Math.min(clearFeet, toLen)
          : Math.max(toLen - clearFeet, 0)
      } else if (junction && nextType === 'COUPLE') {
        // Stop a coupler gap short of the nearest cut member's
        // JUNCTION-side edge — the approach leg ends with the consist's
        // head at coupling distance. (Was LOCO_LENGTH_FT: the lead mover
        // rested a full body-length away from the cut, and on short spans
        // the committed end straddled the entry junction.)
        let nearest = Infinity
        let nearestId: string | undefined
        for (const id of item.carIds ?? []) {
          const p = parked.get(id)
          if (!p || p.trackId !== toTrack.id) continue
          const depth = jEnd === 'first' ? p.startFeet
            : jEnd === 'last' ? toLen - p.endFeet
            : p.offsetFeet
          if (depth < nearest) {
            nearest = depth
            nearestId = id
          }
        }
        if (nearest !== Infinity) {
          coupleSpanFeet = nearest
          coupleNearestId = nearestId
          const stopOriented = Math.max(nearest - COUPLER_GAP_FT, 0)
          exitFeet = jEnd === 'first' ? stopOriented : toLen - stopOriented
        }
      }

      // HARD OCCUPANCY CAP — applies to every leg: the lead mover may
      // never travel past a coupler gap short of the nearest parked body
      // on the destination. This is what stops the loco (or the cut)
      // from traveling through cars already spotted there.
      if (jEnd && nearestOccDepth !== Infinity) {
        const capDepth = Math.max(nearestOccDepth - PACK_GAP_FT, 0)
        const capAB = jEnd === 'first' ? capDepth : toLen - capDepth
        const uncapped = exitFeet ?? (jEnd === 'first' ? toLen : 0)
        const capped = jEnd === 'first'
          ? Math.min(uncapped, capAB)
          : Math.max(uncapped, capAB)
        if (Math.abs(capped - uncapped) > 0.5) {
          stale.push(
            `step ${stepIndex}: leg stops a coupler gap short of parked cars on '${trackName(toTrack.id)}'`
          )
        }
        exitFeet = capped
      }

      const legRes = buildTravelLegPath({
        fromTrack,
        toTrack,
        junctionPoint: junction ?? null,
        enterFromOffsetFeet: enterAB,
        toTrackExitFeet: exitFeet,
      })
      if (!legRes.ok) {
        stale.push(`step ${stepIndex}: ${legRes.error}`)
        fatal = true
        break
      }
      const leg = legRes.leg
      if (leg.junctionGapExceeded) {
        stale.push(`step ${stepIndex}: junction gap ${leg.junctionGapDeg.toExponential(1)} deg`)
      }

      // Mover arc positions at leg start — each arc anchors the mover's
      // HEAD (junction-side edge): endFeet for an A→B leg, startFeet for
      // a flipped B→A leg.
      const moverArcs = new Map<string, number>()
      for (const id of movers) {
        const p = id === LOCO ? locoPos! : parked.get(id)!
        const fromLen = fromTrack!.properties?.lengthFeet ?? 2000
        moverArcs.set(id, (flipped ? fromLen - p.startFeet : p.endFeet) - tailStart)
      }
      // First leg, no prior loco position. If a consist already rides the
      // from-track (EDI leg 1: inbound cut parked at the couple junction,
      // yard loco coupled junction-side), the leg is a PULL-OUT — seat
      // the loco AHEAD of the lead mover. A bare loco enters at the
      // trimmed path start as before. F-SWL-2: an explicit consistHeading
      // overrides the pull-out assumption — CARS_FIRST seats the loco
      // BEHIND the tail mover (shove start).
      if (locoPos == null) {
        movers.add(LOCO)
        if (moverArcs.size > 0) {
          if (step.consistHeading === 'CARS_FIRST') {
            const tailArc = Math.min(...moverArcs.values())
            moverArcs.set(LOCO, Math.max(tailArc - COUPLER_GAP_FT - LOCO_LENGTH_FT, 0))
          } else {
            const leadArc = Math.max(...moverArcs.values())
            moverArcs.set(LOCO, leadArc + COUPLER_GAP_FT + LOCO_LENGTH_FT)
          }
        } else {
          moverArcs.set(LOCO, 0)
        }
      }

      // Insufficient entry span (destination-delivery defect). The leg
      // must leave the cut between the junction and the nearest
      // standing body; when the clear span is shorter than what this
      // leg has to place, drawing it leaves movers straddling the
      // switch and a fabricated partial drop. Halt here — the timeline
      // ends at the last drawable state and the banner explains why.
      // A shove (loco trails) legitimately keeps the loco on the
      // source, so only the cars need the span; a pull-in seats the
      // loco on the destination too.
      if (destStopDepthFeet !== undefined) {
        const carsOnLegFeet = [...movers]
          .filter(id => id !== LOCO)
          .reduce((acc, id) => acc + lenOf(id), 0)
        const locoLeads =
          moverArcs.has(LOCO) &&
          moverArcs.get(LOCO) === Math.max(...moverArcs.values())
        const requiredFeet = carsOnLegFeet + (locoLeads ? LOCO_LENGTH_FT : 0)
        if (requiredFeet > destStopDepthFeet) {
          halt(
            itemIndex,
            item,
            `cannot complete the drop on '${trackName(toTrack.id)}' — ${Math.round(destStopDepthFeet)} ft clear span, ${Math.round(requiredFeet)} ft needed` +
              (destBlockerId ? ` ('${carMark(destBlockerId)}' stands at the entry)` : '')
          )
          break
        }
      }

      // Insufficient approach span (pickup defect — counterpart of the
      // drop-span check above): the consist's head rests a coupler gap
      // short of the cut, so the leg needs every mover that enters the
      // track to fit inside the clear span. A shove leaves the loco on
      // the source; a loco-led approach needs it inside. When the span
      // is too short the committed end would straddle the entry end —
      // name the real blocker rather than the generic straddle halt.
      if (coupleSpanFeet !== undefined) {
        const carsOnLegFeet = [...movers]
          .filter(id => id !== LOCO)
          .reduce((acc, id) => acc + lenOf(id), 0)
        const locoLeads =
          moverArcs.has(LOCO) &&
          moverArcs.get(LOCO) === Math.max(...moverArcs.values())
        const requiredFeet =
          carsOnLegFeet + (locoLeads ? LOCO_LENGTH_FT : 0) + COUPLER_GAP_FT
        if (requiredFeet > coupleSpanFeet) {
          halt(
            itemIndex,
            item,
            `cannot couple on '${trackName(toTrack.id)}' — ${Math.round(coupleSpanFeet)} ft clear span, ${Math.round(requiredFeet)} ft needed` +
              (coupleNearestId ? ` ('${carMark(coupleNearestId)}' stands nearest the entry)` : '')
          )
          break
        }
      }

      // The leg ends when the LEAD mover (largest arc = closest to the
      // junction) reaches the leg end. Keying on the loco collapsed
      // leading cars onto the loco's endpoint during a shove.
      const leadArc = Math.max(0, ...moverArcs.values())
      // Clamp to the hard stop when a parked body blocks the source-track
      // sweep — spanFeet ≥ 1 keeps a marker dwell so the stall is visible.
      const rawLegFeet = Math.max(leg.totalFeet - leadArc, 1)
      const hardStopLeg = Math.max(hardStopFeet, 1)
      const legFeet = Math.min(rawLegFeet, hardStopLeg)

      // Invariant (d): a leg the hard stop clamps to ~no progress means
      // the consist cannot leave this track — every later step was
      // planned assuming it did. Stop rather than animate the divergence
      // (a dropped car mid-track would compound it).
      if (hardStopLeg <= 2 && rawLegFeet > 2) {
        halt(
          itemIndex,
          item,
          `leg stalls immediately — '${hardStopperId != null ? carMark(hardStopperId) : 'a parked body'}' blocks the consist on '${trackName(fromTrack!.id)}'`
        )
        break
      }

      // Post-leg state: movers park where the leg left them. The loco only
      // moves when it was actually on this leg — a leg whose from-track
      // the loco isn't on must not teleport it to the leg end.
      const endPos = new Map(parked)
      if (moverArcs.has(LOCO)) {
        const locoEnd = positionAtFeet(leg, (moverArcs.get(LOCO) ?? 0) + legFeet)
        if (locoEnd) {
          if (straddlesTrackEnd(locoEnd, LOCO_LENGTH_FT)) {
            // An overhang on the JUNCTION-facing end of the track the loco
            // ended on is a switch foul: the consist is placed but the loco
            // can't clear the switch it just crossed (e.g. a shove into a
            // short clear span). Committed positions are clamped inside the
            // leg, so this is the reachable straddle — non-junction-facing
            // overhangs are defensive and keep the generic track-end wording.
            const endTrack = geo.tracks.get(locoEnd.trackId)
            const segLen = endTrack?.properties?.lengthFeet ?? 2000
            const iv = bodyInterval(locoEnd, LOCO_LENGTH_FT)
            const junctionFacing =
              junction && endTrack ? junctionEnd(endTrack, junction) : undefined
            const foulsSwitch =
              (junctionFacing === 'first' && iv.startFeet < -0.5) ||
              (junctionFacing === 'last' && iv.endFeet > segLen + 0.5)
            halt(
              itemIndex,
              item,
              foulsSwitch
                ? `cannot complete the leg — the consist fits, but the locomotive cannot clear '${switchName(step.switchId)}' and would rest foul of the switch on '${trackName(locoEnd.trackId)}'`
                : `cannot complete the leg — the locomotive would rest across the track end on '${trackName(locoEnd.trackId)}'`
            )
            break
          }
          locoPos = {
            trackId: locoEnd.trackId,
            offsetFeet: locoEnd.offsetFeet,
            headAngleDeg: locoEnd.angleDeg,
            bodyDir: 'trail',
            ...bodyInterval(locoEnd, LOCO_LENGTH_FT),
          }
          endPos.set(LOCO, locoPos)
        }
      }
      for (const id of movers) {
        if (id === LOCO) continue
        const pos = positionAtFeet(leg, (moverArcs.get(id) ?? 0) + legFeet)
        if (pos) {
          if (straddlesTrackEnd(pos, lenOf(id))) {
            halt(
              itemIndex,
              item,
              `cannot complete the leg — '${carMark(id)}' would rest across the track end on '${trackName(pos.trackId)}'`
            )
            break
          }
          endPos.set(id, {
            trackId: pos.trackId,
            offsetFeet: pos.offsetFeet,
            headAngleDeg: pos.angleDeg,
            bodyDir: 'trail',
            ...bodyInterval(pos, lenOf(id)),
          })
        }
      }
      if (inconsistency) break

      const stepEntry: TimelineStep = {
        itemIndex,
        stepIndex,
        step,
        startFeet: cursor,
        spanFeet: Math.max(legFeet, 1),
        leg,
        moverArcs,
        movers,
        parkedAtStart: new Map(parked),
        consistAtStart: new Set(consist),
        switchStatesAtStart: { ...switchStates },
      }
      steps.push(stepEntry)
      cursor += stepEntry.spanFeet
      parked.clear()
      for (const [k, v] of endPos) parked.set(k, v)
    }
    if (inconsistency || fatal) break

    // Invariant (f): the pass must leave its named cars on the target —
    // a car still in the consist rides with the loco into the next pass
    // and is exempt here, but a dropped car off the target means the
    // world diverged from the plan; later passes cannot be trusted.
    {
      const leftover = (item.carIds ?? []).find(id => {
        const p = parked.get(id)
        return p != null && p.trackId !== item.targetTrackId && !consist.has(id)
      })
      if (leftover) {
        halt(
          itemIndex,
          item,
          `pass did not reach its destination — '${carMark(leftover)}' rests on '${trackName(parked.get(leftover)!.trackId)}', expected '${trackName(item.targetTrackId)}'`
        )
      }
    }
    if (endPass(itemIndex)) break
  }

  if (steps.length === 0 && !fatal) {
    if (parked.size > 0 || castCarIds.size > 0) {
      // All-passes-skipped (or empty) with a world to show: one anchor
      // dwell renders the parked state behind the blocked banner instead
      // of leaving snapshotAt nothing to index.
      steps.push({
        itemIndex: -1,
        stepIndex: -1,
        step: { type: 'IDLE', description: 'no animatable steps' } as MicroStep,
        startFeet: 0,
        spanFeet: 1,
        parkedAtStart: new Map(parked),
        consistAtStart: new Set(consist),
        switchStatesAtStart: { ...switchStates },
      })
    }
    stale.push('no animatable steps (empty or fully blocked list)')
  }

  return {
    ok: !fatal,
    timeline: fatal
      ? undefined
      : {
          steps,
          totalFeet: Math.max(cursor, 1),
          staleReasons: stale,
          fatal,
          castCarIds,
          approxCarIds,
          itemCarIds,
          blockedPasses,
          inconsistency,
        },
    staleReasons: stale,
    fatal,
    blockedPasses,
    inconsistency,
  }
}

/**
 * Snapshot the consist at `feet` along the timeline. Deterministic — safe to
 * call every animation frame.
 */
export const snapshotAt = (
  timeline: PreviewTimeline,
  geo: PreviewGeo,
  feet: number
): PreviewSnapshot => {
  if (timeline.steps.length === 0) {
    // Fully empty timeline (no steps, no world) — nothing to render.
    return {
      loco: null,
      cars: [],
      ambientCars: [],
      activeStepIndex: -1,
      activeItemIndex: -1,
      activeMicroStepIndex: -1,
      activeStep: null,
      activeSyntheticApproachFeet: undefined,
      shoving: false,
      switchStates: {},
    }
  }
  const clamped = Math.max(0, Math.min(feet, timeline.totalFeet))
  let idx = timeline.steps.findIndex(s => clamped < s.startFeet + s.spanFeet)
  if (idx < 0) idx = timeline.steps.length - 1
  const ts = timeline.steps[Math.max(idx, 0)]

  const t = clamped - ts.startFeet
  const loco: { pos: LegPosition | null } = { pos: null }
  const cars: PreviewCarSnapshot[] = []

  const place = (id: string): LegPosition | null => {
    if (ts.leg && ts.movers?.has(id)) {
      const arc = ts.moverArcs?.get(id) ?? 0
      return positionAtFeet(ts.leg, arc + t)
    }
    const p = ts.parkedAtStart.get(id)
    if (!p) return null
    const track = geo.tracks.get(p.trackId)
    if (!track) return null
    const pos = pointOnTrackAB(track, p.offsetFeet)
    // Dropped cars keep the heading they were parked facing; inventory
    // cars (no recorded heading) fall back to the track's A→B tangent.
    if (pos && p.headAngleDeg != null) pos.angleDeg = p.headAngleDeg
    return pos
  }

  loco.pos = place(LOCO)
  if (!loco.pos && ts.leg && ts.moverArcs?.has(LOCO)) {
    loco.pos = positionAtFeet(ts.leg, (ts.moverArcs.get(LOCO) ?? 0) + t)
  }

  for (const id of timeline.castCarIds) {
    const pos = place(id)
    if (!pos) continue
    const p = ts.parkedAtStart.get(id)
    const bodyDir = ts.leg && ts.movers?.has(id) ? 'trail' : p?.bodyDir ?? 'forward'
    cars.push({
      bodyDir,
      carId: id,
      pos,
      inConsist: ts.consistAtStart.has(id),
      approx: timeline.approxCarIds.has(id),
    })
  }

  // F-UI-1: ambient bodies — every parked entity that is not the loco
  // and never joins the consist. They occupy track space (and may be
  // the reason a pass blocked); render them so the map explains the
  // stop rather than showing an empty track.
  const ambientCars: PreviewCarSnapshot[] = []
  for (const [id, p] of ts.parkedAtStart) {
    if (id === LOCO || timeline.castCarIds.has(id)) continue
    const track = geo.tracks.get(p.trackId)
    if (!track) continue
    const pos = pointOnTrackAB(track, p.offsetFeet)
    if (!pos) continue
    if (p.headAngleDeg != null) pos.angleDeg = p.headAngleDeg
    ambientCars.push({
      ambient: true,
      bodyDir: p.bodyDir ?? 'forward',
      carId: id,
      pos,
      inConsist: false,
      approx: false,
    })
  }

  // Shove detection: REVERSE seen earlier in the same item and no reset — the
  // consist is being pushed when the last non-TRAVEL direction change flipped.
  let shoving = false
  for (const s of timeline.steps) {
    if (s.startFeet > clamped) break
    if (s.step.type === 'REVERSE') shoving = !shoving
  }

  return {
    loco: loco.pos,
    cars,
    ambientCars,
    activeStepIndex: Math.max(idx, 0),
    activeItemIndex: ts.itemIndex,
    activeMicroStepIndex: ts.stepIndex,
    activeStep: ts.step,
    activeSyntheticApproachFeet: ts.syntheticApproachFeet,
    shoving,
    switchStates: { ...ts.switchStatesAtStart },
  }
}

/** Convenience for callers that just need "can we play?" */
export const isPlayable = (r: BuildTimelineResult): boolean => r.ok && !!r.timeline

export { DEFAULT_LOCO_OFFSET_FT }
